import { TaxEntryType, TaxInfoEntry } from '@/types/api/tax-info';

import { getForeignNonTaxResidencyEntries } from '../ForeignNonTaxResidencySection/utils';
import { getForeignTaxResidencyEntries } from '../ForeignTaxResidencySection/utils';
import { getUsTaxEntry } from '../QuestionUSTaxReturn/utils';
import { TaxQuestionAnswers } from './types';

export const QUESTION_2_TAX_ENTRY_TYPES = [
  TaxEntryType.NO_TAX,
  TaxEntryType.FOREIGN_TAX_RESIDENCY,
  TaxEntryType.FOREIGN_NON_TAX_RESIDENCY,
  TaxEntryType.FOREIGN_INDICIA_EXPLANATION,
] as const;

export function getNoTaxEntry(taxInfo?: TaxInfoEntry[]) {
  return taxInfo?.find(entry => entry.taxEntryType === TaxEntryType.NO_TAX);
}

export function getForeignIndiciaExplanationEntries(taxInfo?: TaxInfoEntry[]) {
  return (
    taxInfo?.filter(
      entry => entry.taxEntryType === TaxEntryType.FOREIGN_INDICIA_EXPLANATION,
    ) ?? []
  );
}

function isTaxEntryCompleted(entry: TaxInfoEntry) {
  return entry.isCompleted === true;
}

export function markTaxInfoEntriesAsCompleted(
  entries: TaxInfoEntry[],
): TaxInfoEntry[] {
  return entries.map(entry => ({
    ...entry,
    isCompleted: true,
  }));
}

export function replaceOwnedTaxEntries(
  existingEntries: TaxInfoEntry[],
  nextEntries: TaxInfoEntry[],
  ownedTypes: TaxEntryType[],
): TaxInfoEntry[] {
  const firstOwnedEntryIndex = existingEntries.findIndex(entry =>
    ownedTypes.includes(entry.taxEntryType),
  );

  if (firstOwnedEntryIndex === -1) {
    return [...existingEntries, ...nextEntries];
  }

  const result: TaxInfoEntry[] = [];
  let inserted = false;

  existingEntries.forEach(entry => {
    if (ownedTypes.includes(entry.taxEntryType)) {
      if (!inserted) {
        result.push(...nextEntries);
        inserted = true;
      }

      return;
    }

    result.push(entry);
  });

  return result;
}

function getUsTaxEntries(taxInfo?: TaxInfoEntry[]) {
  return (
    taxInfo?.filter(entry => entry.taxEntryType === TaxEntryType.US_TAX) ?? []
  );
}

function isUsTaxQuestionComplete(taxInfo?: TaxInfoEntry[]) {
  const usTaxEntries = getUsTaxEntries(taxInfo);

  return usTaxEntries.length > 0 && usTaxEntries.every(isTaxEntryCompleted);
}

export function isTaxInfoComplete(taxInfo?: TaxInfoEntry[]) {
  if (!taxInfo?.length) {
    return false;
  }

  const uniqueTaxEntryTypes = new Set(
    taxInfo
      .map(entry => entry.taxEntryType)
      .filter((type): type is TaxEntryType => type !== undefined),
  );

  return uniqueTaxEntryTypes.size >= 2 && taxInfo.every(isTaxEntryCompleted);
}

export function getTaxInfoStartingQuestionIndex(taxInfo?: TaxInfoEntry[]) {
  if (!isUsTaxQuestionComplete(taxInfo)) {
    return 0;
  }

  if (!isTaxInfoComplete(taxInfo)) {
    return 1;
  }

  return 0;
}

export function deriveTaxQuestionAnswers(
  taxInfo?: TaxInfoEntry[],
): TaxQuestionAnswers {
  const usTaxReturnRequired = getUsTaxEntry(taxInfo)?.usTaxReturnRequired;

  const foreignTaxResidencyEntries = getForeignTaxResidencyEntries(taxInfo);

  if (foreignTaxResidencyEntries.length) {
    return {
      usTaxReturnRequired,
      hasForeignConnection:
        foreignTaxResidencyEntries[0].hasResidenceInOtherCountry ?? true,
      isForeignTaxResident: true,
    };
  }

  if (getForeignNonTaxResidencyEntries(taxInfo).length) {
    return {
      usTaxReturnRequired,
      hasForeignConnection: true,
      isForeignTaxResident: false,
    };
  }

  if (
    getForeignIndiciaExplanationEntries(taxInfo).length ||
    taxInfo?.some(entry => entry.taxEntryType === TaxEntryType.NO_TAX)
  ) {
    return {
      usTaxReturnRequired,
      hasForeignConnection: false,
      isForeignTaxResident: false,
    };
  }

  return { usTaxReturnRequired };
}
